import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import infrastructureMonitoringService from '../../../services/infrastructureMonitoringService';

const DatabaseHealthPanel = ({ timeRange }) => {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadDatabaseMetrics();
  }, [timeRange, filter]);

  const loadDatabaseMetrics = async () => {
    try {
      setLoading(true);
      const filters = { timeRange, limit: 50 };
      if (filter !== 'all') {
        filters.healthStatus = filter;
      }
      const data = await infrastructureMonitoringService?.getDatabasePerformanceMetrics(filters);
      setMetrics(data);
    } catch (error) {
      console.error('Error loading database metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getHealthColor = (status) => {
    const colors = {
      optimal: 'text-green-600 bg-green-50',
      warning: 'text-yellow-600 bg-yellow-50',
      critical: 'text-red-600 bg-red-50'
    };
    return colors?.[status] || 'text-gray-600 bg-gray-50';
  };

  const getExecutionTimeColor = (ms) => {
    if (ms < 100) return 'text-green-600';
    if (ms < 300) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div>
      {/* Filter Controls */}
      <div className="mb-6 flex items-center gap-4">
        <span className="text-sm font-medium text-gray-700">Filter by Health:</span>
        <div className="flex gap-2">
          {['all', 'optimal', 'warning', 'critical']?.map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`
                px-4 py-2 text-sm font-medium rounded-md transition-colors
                ${filter === status
                  ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
            >
              {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader" className="w-6 h-6 text-blue-600 animate-spin" />
        </div>
      ) : metrics?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="Database" className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No database performance data available</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Query Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Execution Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rows Affected
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Connection Pool
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Throughput
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Health Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Recorded At
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {metrics?.map((metric) => {
                const poolUtilization = metric?.connectionPoolTotal > 0
                  ? ((metric?.connectionPoolActive / metric?.connectionPoolTotal) * 100)?.toFixed(0)
                  : 0;

                return (
                  <tr key={metric?.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{metric?.queryName}</div>
                      {metric?.optimizationNotes && (
                        <div className="text-xs text-gray-500 mt-1">{metric?.optimizationNotes}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-800">
                        {metric?.queryType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-semibold ${getExecutionTimeColor(metric?.executionTimeMs)}`}>
                        {metric?.executionTimeMs}ms
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {metric?.rowsAffected?.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {metric?.connectionPoolActive}/{metric?.connectionPoolTotal}
                      </div>
                      <div className="text-xs text-gray-500">{poolUtilization}% utilized</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {metric?.transactionThroughput} TPS
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${getHealthColor(metric?.healthStatus)}`}>
                        {metric?.healthStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(metric?.recordedAt)?.toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DatabaseHealthPanel;